import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { useRef, useState } from 'react';
import {
    KeyboardAvoidingView,
    Platform,
    type ScrollView as RNScrollView,
} from 'react-native';
import { NestableScrollContainer } from 'react-native-draggable-flatlist';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenContainer,
    Icon,
    InfoIcon,
    isWeb,
    Separator,
    Shadows,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { v4 as uuidv4 } from 'uuid';

import useShouldSeeIncomeSplit from '../../../hooks/income-split/useShouldSeeIncomeSplit';
import useGetAllBankAccountDetails from '../../../hooks/useGetAllBankAccountDetails';
import {
    currentlyEditingConfigAtom,
    selectedIncomeAtom,
    selectedIncomeBankAccountAtom,
    selectedRuleAtom,
} from '../../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../../state/SpacesAtoms';
import {
    type PaycheckRoutingRule,
    PaycheckRoutingRulesMode,
    PaycheckRoutingRuleType,
} from '../../../types/PaycheckRouting';
import { BankDisclosureShortFootnote } from '../../Disclosures';
import DetectedIncomeCard from '../common/DetectedIncomeCard';
import { getClosedAccountsInConfig } from '../helpers';
import RemainingRules from './RemainingRules';
import PrimaryRuleEditorSheet from './rule-editor/primary/PrimaryRuleEditor';
import SortableRulesList from './SortableRulesList';

const IncomeAccountRouting = () => {
    const { mode } = useLocalSearchParams<{ mode: PaycheckRoutingRulesMode }>();

    const selectedIncome = useAtomValue(selectedIncomeAtom);
    const selectedIncomeBankAccount = useAtomValue(
        selectedIncomeBankAccountAtom,
    );
    const bankAccountDetails = useAtomValue(bankAccountDisplayDetails);
    const [currentlyEditingConfig, setCurrentlyEditingConfig] = useAtom(
        currentlyEditingConfigAtom,
    );
    const [selectedRule, setSelectedRule] = useAtom(selectedRuleAtom);

    const [isAddingRule, setIsAddingRule] = useState(false);
    const [isEditingRule, setIsEditingRule] = useState(false);
    const [isEditingRemainingRules, setIsEditingRemainingRules] =
        useState(false);

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    const isAddingOrEditingRule = isAddingRule || isEditingRule;
    // if we're editing a pre-existing rule set, we don't want to automatically create any percentage rules
    const [hasCreatedRules, setHasCreatedRules] = useState(
        mode === PaycheckRoutingRulesMode.EDIT,
    );

    const closeEditor = () => {
        setIsAddingRule(false);
        setIsEditingRule(false);
        setSelectedRule(null);
    };

    const { closedAccountDetails } = useGetAllBankAccountDetails(false);

    const scrollViewRef = useRef<RNScrollView | null>(null);

    if (shouldSeeIncomeSplit ?? !selectedIncome ?? !selectedIncomeBankAccount) {
        return <Redirect href="/home/banking" />;
    }

    const closedAccountsInConfig = getClosedAccountsInConfig({
        config: currentlyEditingConfig,
        closedAccountDetails,
    });

    return (
        <KeyboardAvoidingView
            style={{
                flex: 1,
                width: '100%',
            }}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            keyboardVerticalOffset={0}
            ignoreIOSKeyboardWillChangeEvents={true}
        >
            <FullScreenContainer
                scrollDisabled={true}
                header={
                    <>
                        <BackButton
                            fallbackRoute="/"
                            iconType={BackButtonIconType.LEFT}
                        />
                        <BodyLargeRegular>
                            Paycheck split settings
                        </BodyLargeRegular>
                        <View width={32} height={32} />
                    </>
                }
                separator={
                    !isAddingRule && !isEditingRule && !isEditingRemainingRules
                }
                footer={
                    !isAddingRule &&
                    !isEditingRule &&
                    !isEditingRemainingRules && (
                        <YStack gap={12}>
                            <Button
                                disabled={
                                    [
                                        ...(currentlyEditingConfig?.rules
                                            ?.primary ?? []),
                                        ...(currentlyEditingConfig?.rules
                                            ?.remaining ?? []),
                                    ].length < 1
                                }
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                text="Continue"
                                onPress={() => {
                                    router.navigate(
                                        '/banking/paycheck-routing/review-routing',
                                    );
                                }}
                            />
                            <BankDisclosureShortFootnote />
                        </YStack>
                    )
                }
            >
                <NestableScrollContainer
                    onContentSizeChange={(_, h) => {
                        if (isAddingRule)
                            scrollViewRef.current?.scrollTo({ y: h });
                    }}
                    ref={scrollViewRef}
                    showsVerticalScrollIndicator={isWeb}
                    contentContainerStyle={{
                        flexGrow: 1,
                    }}
                >
                    <YStack marginTop={35}>
                        {closedAccountsInConfig.length > 0 && (
                            <View
                                backgroundColor="$white"
                                style={Shadows.SMALL}
                                borderWidth={1}
                                borderColor="$gray200"
                                borderRadius={20}
                                paddingHorizontal={12}
                                paddingVertical={14}
                                marginBottom={26}
                            >
                                <XStack alignItems="center" gap={8}>
                                    <View>
                                        <Icon
                                            icon={InfoIcon}
                                            color="$orange900"
                                            size={20}
                                        />
                                    </View>

                                    <BodyMediumRegular color="$gray1000">
                                        {closedAccountsInConfig
                                            .map((account) => account.nickname)
                                            .join(', ')}{' '}
                                        account has been closed.{'\n'}Please
                                        update the ruleset.
                                    </BodyMediumRegular>
                                </XStack>
                            </View>
                        )}
                        <BodyMediumRegular marginTop={12} color="$gray1000">
                            Rules will execute in order from top to bottom
                            subject to available funds
                        </BodyMediumRegular>
                    </YStack>
                    <YStack marginTop={24}>
                        <DetectedIncomeCard
                            income={selectedIncome}
                            bankAccountDetails={selectedIncomeBankAccount}
                        />
                        <SortableRulesList
                            setSelectedRule={setSelectedRule}
                            isAddingOrEditingRule={isAddingOrEditingRule}
                            isEditingRemainingRules={isEditingRemainingRules}
                            setIsAddingRule={setIsAddingRule}
                            setIsEditingRule={setIsEditingRule}
                            closedAccountsInConfig={closedAccountsInConfig}
                        />
                        {selectedRule && (
                            <PrimaryRuleEditorSheet
                                bankAccounts={bankAccountDetails}
                                selectedRule={selectedRule}
                                updateSelectedRule={(updatedRule) => {
                                    setSelectedRule(updatedRule);
                                }}
                                isEditingRule={isEditingRule}
                                isOpen={isAddingOrEditingRule}
                                onClose={() => {
                                    closeEditor();
                                }}
                                onSave={() => {
                                    const { primary = [], remaining = [] } =
                                        currentlyEditingConfig?.rules ?? {};

                                    const updatedPrimaryRules = isAddingRule
                                        ? [
                                              ...primary,
                                              selectedRule as PaycheckRoutingRule,
                                          ]
                                        : primary.map((rule) =>
                                              rule.id === selectedRule?.id
                                                  ? (selectedRule as PaycheckRoutingRule)
                                                  : rule,
                                          );

                                    const updatedRulesConfig = {
                                        ...currentlyEditingConfig,
                                        rules: {
                                            primary: updatedPrimaryRules,
                                            remaining,
                                        },
                                    };

                                    // if we're creating a rule for the first time, also create a default remaining percentage rule
                                    if (!hasCreatedRules) {
                                        setHasCreatedRules(true);
                                        updatedRulesConfig.rules.remaining = [
                                            {
                                                id: uuidv4(),
                                                type: PaycheckRoutingRuleType.PERCENTAGE,
                                                amount: 100,
                                                order:
                                                    updatedPrimaryRules.length +
                                                    1,
                                                accountId:
                                                    selectedIncomeBankAccount.bankAccountId,
                                            },
                                        ];
                                    }

                                    setCurrentlyEditingConfig(
                                        updatedRulesConfig,
                                    );
                                    closeEditor();
                                }}
                                onCancel={() => {
                                    closeEditor();
                                }}
                            />
                        )}
                        {Boolean(
                            (currentlyEditingConfig?.rules?.primary ?? [])
                                .length || hasCreatedRules,
                        ) && (
                            <>
                                <YStack
                                    marginTop={
                                        isAddingOrEditingRule ||
                                        isEditingRemainingRules
                                            ? 24
                                            : undefined
                                    }
                                >
                                    <Separator />
                                </YStack>
                                <YStack marginTop={24} marginBottom={16}>
                                    <RemainingRules
                                        isEditingRemainingRules={
                                            isEditingRemainingRules
                                        }
                                        isEditingRule={isAddingOrEditingRule}
                                        setIsEditing={
                                            setIsEditingRemainingRules
                                        }
                                    />
                                </YStack>
                            </>
                        )}
                    </YStack>
                </NestableScrollContainer>
            </FullScreenContainer>
        </KeyboardAvoidingView>
    );
};

export default IncomeAccountRouting;
